import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRetryableQuery } from '@/hooks/useRetryableQuery';

interface DailySummary {
  created: number;
  closed: number;
  avgResponseTime: number | null; // in minutes
}

interface UseTodayStatsOptions {
  workspaceId: string | undefined;
}

interface UseTodayStatsReturn {
  data: DailySummary;
  loading: boolean;
  error: Error | null;
  retry: () => void;
}

export function useTodayStats({ workspaceId }: UseTodayStatsOptions): UseTodayStatsReturn {
  const fetchDailySummary = useCallback(async (): Promise<DailySummary> => {
    if (!workspaceId) {
      return { created: 0, closed: 0, avgResponseTime: null };
    }

    // Get start of today in UTC
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayISO = today.toISOString();

    // Count tickets created today
    const { count: createdCount } = await supabase
      .from('tickets')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .gte('created_at', todayISO);

    // Count tickets closed today (via ticket_activities)
    const { count: closedCount } = await supabase
      .from('ticket_activities')
      .select('*, tickets!inner(workspace_id)', { count: 'exact', head: true })
      .eq('tickets.workspace_id', workspaceId)
      .eq('action', 'status_changed')
      .eq('new_value', 'closed')
      .gte('created_at', todayISO);

    // Calculate average response time for tickets created today
    const { data: ticketsWithFirstResponse } = await supabase
      .from('tickets')
      .select(`
        id,
        created_at,
        ticket_messages!inner(created_at, sender_type)
      `)
      .eq('workspace_id', workspaceId)
      .gte('created_at', todayISO)
      .eq('ticket_messages.sender_type', 'agent')
      .order('created_at', { ascending: true, referencedTable: 'ticket_messages' });

    let avgResponseTime: number | null = null;
    
    if (ticketsWithFirstResponse && ticketsWithFirstResponse.length > 0) {
      const responseTimes: number[] = [];
      const ticketFirstResponses = new Map<string, number>();
      
      for (const ticket of ticketsWithFirstResponse) {
        const messages = ticket.ticket_messages as { created_at: string; sender_type: string }[];
        if (messages && messages.length > 0 && !ticketFirstResponses.has(ticket.id)) {
          const firstAgentMessage = messages[0];
          const ticketCreated = new Date(ticket.created_at).getTime();
          const agentReplied = new Date(firstAgentMessage.created_at).getTime();
          const diffMinutes = (agentReplied - ticketCreated) / (1000 * 60);
          ticketFirstResponses.set(ticket.id, diffMinutes);
          responseTimes.push(diffMinutes);
        }
      }
      
      if (responseTimes.length > 0) {
        avgResponseTime = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
      }
    }

    return {
      created: createdCount || 0,
      closed: closedCount || 0,
      avgResponseTime,
    };
  }, [workspaceId]);

  const { data, loading, error, retry } = useRetryableQuery<DailySummary>({
    queryFn: fetchDailySummary,
    enabled: !!workspaceId,
  });

  return {
    data: data ?? { created: 0, closed: 0, avgResponseTime: null },
    loading,
    error,
    retry,
  };
}
